import React, { useState, useMemo, useEffect } from "react";
import AIReportSkeleton from "../../additional_components/common/AIReportSkeleton";
import BackButton from "../../components/Common/BackButton";
import ChatbotUI from "../../additional_components/common/Modals/Chat";
import { motion } from "framer-motion";
import { Activity, TrendingUp, TrendingDown, Calendar, ArrowUpRight, ArrowDownRight, Lightbulb, AlertTriangle, Zap, Send } from "lucide-react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Card, CardBody, CardHeader, CardTitle, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";
import classnames from "classnames";
import { useLocation } from "react-router-dom";
import { ai_api } from "../../utils/variables";

// ── API Integration ───────────────────────────────────────────────
// The AI service (ai.keyquest.io) reports plan/tier restrictions as
// { error: "Please upgrade your plan." } rather than the tracker API's
// { status, action, current_tier } shape.
const isUpgradeResponse = data =>
    !!data?.error && /upgrade/i.test(data.error)

const fetchForecastingData = async (appId, token) => {
    try {
        const headers = { "Content-Type": "application/json" }
        if (token) headers.Authorization = `Bearer ${token}`

        const response = await fetch(`${ai_api}/forecasting/forecast/`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ app_id: appId })
        });

        // Parse the body even on non-2xx responses — the backend returns the
        // "please upgrade" payload with a non-200 status, and discarding it here
        // is what left the caller with no way to detect it (see isUpgradeResponse).
        const result = await response.json().catch(() => null)

        if (!response.ok) {
            if (isUpgradeResponse(result)) return result
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return result;
    } catch (error) {
        console.error('Error fetching forecasting data:', error);
        throw error;
    }
};

const hasRequiredData = (data) =>
    data &&
    Array.isArray(data.actual_downloads) && data.actual_downloads.length > 0 &&
    Array.isArray(data.downloads_forecast) && data.downloads_forecast.length > 0;

// ── Helpers ────────────────────────────────────────────────────────
const fmt = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtNum = (n) =>
    n >= 1_000_000
        ? `${(n / 1_000_000).toFixed(2)}M`
        : n >= 1_000
            ? `${(n / 1_000).toFixed(1)}K`
            : n.toFixed(0);

const fmtCurrency = (n) =>
    n >= 1_000_000
        ? `$${(n / 1_000_000).toFixed(2)}M`
        : n >= 1_000
            ? `$${(n / 1_000).toFixed(1)}K`
            : `$${n.toFixed(0)}`;

// ── Custom Tooltip ─────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, isCurrency }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ backgroundColor: "#1e1e2d", border: "1px solid #323246", borderRadius: "8px", padding: "8px 12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", color: "#fff", fontSize: "12px" }}>
            <p style={{ fontWeight: 500, margin: "0 0 4px 0", color: "#a0a0b0" }}>{label}</p>
            {payload.map((p) => (
                <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0" }}>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: p.color }} />
                    <span style={{ color: "#a0a0b0" }}>{p.name}:</span>
                    <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
                        {isCurrency ? fmtCurrency(p.value) : fmtNum(p.value)}
                    </span>
                </div>
            ))}
        </div>
    );
};

// ── Component ──────────────────────────────────────────────────────
const Forecasting = ({ onBack, appName: propAppName, onUpgradeRequired }) => {
    const [activeTab, setActiveTab] = useState("downloads");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [aiQuery, setAiQuery] = useState("");
    const [askOpen, setAskOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const toggleAsk = () => setAskOpen(prev => !prev);
    
    // Get token and app_id following same pattern as gameDashboard
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const gameID = queryParams.get("id");
    const { app_id: locationAppId } = location.state || {};
    
    // Use prop app name if provided, otherwise use generic fallback
    const appName = propAppName || 'App';
    const appId = locationAppId || gameID;

    useEffect(() => {
        const loadForecastData = async () => {
            const token = localStorage.getItem("authToken");
            
            if (!appId || !token) {
                setError('App ID and authentication token are required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const result = await fetchForecastingData(appId, token);
                if (isUpgradeResponse(result)) {
                    if (onUpgradeRequired) onUpgradeRequired();
                    return;
                } else if (result.status === 1 && hasRequiredData(result.data)) {
                    setForecastData(result.data);
                } else if (result.status === 1) {
                    setForecastData(null);
                    setError('empty');
                } else {
                    setError('Failed to load forecast data');
                }
            } catch (err) {
                setError(err.message || 'Failed to load forecast data');
            } finally {
                setLoading(false);
            }
        };

        loadForecastData();
    }, [appId]);

    // Build merged chart data using API response
    const { dlData, revData } = useMemo(() => {
        if (!forecastData) return { dlData: [], revData: [] };

        const {
            actual_downloads = [],
            actual_revenue = [],
            downloads_forecast = [],
            revenue_forecast = [],
            optimized_downloads_forecast,
            optimized_revenue_forecast,
            downloads_range,
            revenue_range
        } = forecastData;

        if (!actual_downloads.length || !downloads_forecast.length) return { dlData: [], revData: [] };

        // Downloads data - show both standard and optimized forecasts
        const recentDl = actual_downloads.slice(-60);
        const lastActualDl = recentDl[recentDl.length - 1];

        const dlMerged = [
            ...recentDl.map((d) => ({
                date: fmt(d.date),
                actual: d.value,
                forecast: undefined,
                optimized: undefined,
                upper: undefined,
                lower: undefined,
            })),
            // bridge point for standard forecast
            {
                date: fmt(lastActualDl.date),
                actual: lastActualDl.value,
                forecast: lastActualDl.value,
                optimized: lastActualDl.value,
                upper: lastActualDl.value + (downloads_range?.max || 0),
                lower: lastActualDl.value - (downloads_range?.min || 0),
            },
            // Standard forecast data
            ...downloads_forecast.map((d) => {
                const optimizedValue = optimized_downloads_forecast?.find(opt => opt.date === d.date)?.value;
                return {
                    date: fmt(d.date),
                    actual: undefined,
                    forecast: d.value,
                    optimized: optimizedValue,
                    upper: d.value + (downloads_range?.max || 0),
                    lower: d.value - (downloads_range?.min || 0),
                };
            }),
            // Optimized forecast data (if dates don't match)
            ...(optimized_downloads_forecast || [])
                .filter(opt => !downloads_forecast.some(d => d.date === opt.date))
                .map((d) => ({
                    date: fmt(d.date),
                    actual: undefined,
                    forecast: undefined,
                    optimized: d.value,
                    upper: d.value + (downloads_range?.max || 0),
                    lower: d.value - (downloads_range?.min || 0),
                })),
        ];

        // Revenue data - show both standard and optimized forecasts
        const recentRev = actual_revenue.slice(-20);
        if (!recentRev.length) return { dlData: dlMerged, revData: [] };
        const lastActualRev = recentRev[recentRev.length - 1];

        const revMerged = [
            ...recentRev.map((d) => ({
                date: fmt(d.date),
                actual: d.value,
                forecast: undefined,
                optimized: undefined,
                upper: undefined,
                lower: undefined,
            })),
            {
                date: fmt(lastActualRev.date),
                actual: lastActualRev.value,
                forecast: lastActualRev.value,
                optimized: lastActualRev.value,
                upper: lastActualRev.value + (revenue_range?.max || 0),
                lower: lastActualRev.value - (revenue_range?.min || 0),
            },
            // Standard forecast data
            ...revenue_forecast.map((d) => {
                const optimizedValue = optimized_revenue_forecast?.find(opt => opt.date === d.date)?.value;
                return {
                    date: fmt(d.date),
                    actual: undefined,
                    forecast: d.value,
                    optimized: optimizedValue,
                    upper: d.value + (revenue_range?.max || 0),
                    lower: d.value - (revenue_range?.min || 0),
                };
            }),
            // Optimized forecast data (if dates don't match)
            ...(optimized_revenue_forecast || [])
                .filter(opt => !revenue_forecast.some(d => d.date === opt.date))
                .map((d) => ({
                    date: fmt(d.date),
                    actual: undefined,
                    forecast: undefined,
                    optimized: d.value,
                    upper: d.value + (revenue_range?.max || 0),
                    lower: d.value - (revenue_range?.min || 0),
                })),
        ];

        return { dlData: dlMerged, revData: revMerged };
    }, [forecastData]);

    const lastDl = forecastData?.actual_downloads?.[forecastData.actual_downloads.length - 1]?.value || 0;
    const forecastEndDl = forecastData?.downloads_forecast?.[forecastData.downloads_forecast.length - 1]?.value || 0;
    const optimizedEndDl = forecastData?.optimized_downloads_forecast?.[forecastData.optimized_downloads_forecast.length - 1]?.value || 0;
    const dlGrowth = lastDl > 0 ? ((forecastEndDl - lastDl) / lastDl) * 100 : 0;
    const optimizedDlGrowth = lastDl > 0 ? ((optimizedEndDl - lastDl) / lastDl) * 100 : 0;

    const lastRev = forecastData?.actual_revenue?.[forecastData.actual_revenue.length - 1]?.value || 0;
    const forecastEndRev = forecastData?.revenue_forecast?.[forecastData.revenue_forecast.length - 1]?.value || 0;
    const revGrowth = lastRev > 0 ? ((forecastEndRev - lastRev) / lastRev) * 100 : 0;

    const summaryCards = [
        {
            title: "Growth Status",
            value: forecastData?.growth_status || "Unknown",
            icon: Activity,
            color: "#ffc107",
            bg: "rgba(255, 193, 7, 0.1)",
        },
        {
            title: "Downloads Forecast",
            value: fmtNum(forecastEndDl),
            sub: `${dlGrowth > 0 ? "+" : ""}${dlGrowth.toFixed(1)}%`,
            icon: dlGrowth >= 0 ? TrendingUp : TrendingDown,
            positive: dlGrowth >= 0,
            color: dlGrowth >= 0 ? "#10b981" : "#ef4444",
            bg: dlGrowth >= 0 ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
        },
        {
            title: "Optimized Forecast",
            value: fmtNum(optimizedEndDl),
            sub: `${optimizedDlGrowth > 0 ? "+" : ""}${optimizedDlGrowth.toFixed(1)}%`,
            icon: ArrowUpRight,
            positive: optimizedDlGrowth >= 0,
            color: "#f59e0b",
            bg: "rgba(245, 158, 11, 0.1)",
        },
        {
            title: "Revenue Forecast",
            value: fmtCurrency(forecastEndRev),
            sub: `${revGrowth > 0 ? "+" : ""}${revGrowth.toFixed(1)}%`,
            icon: revGrowth >= 0 ? TrendingUp : TrendingDown,
            positive: revGrowth >= 0,
            color: revGrowth >= 0 ? "#10b981" : "#ef4444",
            bg: revGrowth >= 0 ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
        },
        {
            title: "Download Uplift",
            value: `+${forecastData?.uplift?.downloads || 0}%`,
            icon: ArrowUpRight,
            color: "#10b981",
            bg: "rgba(16, 185, 129, 0.1)",
        },
    ];

    return (
        <div style={{ backgroundColor: "transparent", color: "#fff" }}>
            <div className="container-fluid" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                {/* No extra top margin/padding here: this view is rendered inside
                    gameDashboard.jsx's `.page-content` wrapper, which already
                    reserves the fixed topbar's clearance - stacking Bootstrap's
                    `mt-5` (48px, !important) plus an inline 50px padding-top on
                    top of that pushed this heading ~98px further down than
                    every other "separate view" (AI CMO, Social Comparison). */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <motion.div
                                animate={{ opacity: [0.7, 1, 0.7] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Activity size={20} color="#F6255A" />
                                <span style={{ fontSize: '12px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '2px', color: '#F6255A' }}>
                                    AI Forecasting
                                </span>
                            </motion.div>
                        </div>
                        <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, letterSpacing: '-0.5px' }}>
                            {appName ? `${appName} - Forecasting Analysis` : 'Forecasting Analysis'}
                        </h1>
                        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '14px', color: '#a0a0a0' }}>
                            <Calendar size={16} />
                            <span>Aug 2025 – Mar 2026 · 7-Day Forecast</span>
                        </div> */}
                    </motion.div>

                    {onBack && <BackButton onClick={onBack} />}
                </div>

                <div className="key_track_sec" style={{ height: "auto", position: "relative", marginBottom: "0", paddingBottom: "0" }}>
                    {loading ? (
                        <AIReportSkeleton
                            showSentiment={false}
                            observationsTitle="AI Forecast Insights"
                            observationsCount={2}
                        />
                    ) : error === 'empty' ? (
                        <div style={{ padding: "60px 20px", textAlign: "center" }}>
                            <Activity size={48} color="#a0a0a0" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <h4 style={{ color: "#fff", marginBottom: "8px" }}>No Forecast Data Available</h4>
                            <p style={{ color: "#a0a0a0", fontSize: "14px", maxWidth: "360px", margin: "0 auto" }}>
                                Forecast data hasn't been generated for this app yet. Please check back later.
                            </p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: "60px 20px", textAlign: "center" }}>
                            <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: "16px" }} />
                            <h4 style={{ color: "#fff", marginBottom: "8px" }}>Failed to Load Forecast</h4>
                            <p style={{ color: "#a0a0a0", fontSize: "14px" }}>{error}</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="row mb-4">
                                {summaryCards.map((card, i) => (
                                    <motion.div
                                        key={card.title}
                                        className="col-md-3 col-sm-6 mb-3"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.08, duration: 0.5 }}
                                    >
                                        <Card style={{ height: "100%", backgroundColor: "rgba(23, 24, 33, 1)", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(4px)" }}>
                                            <CardBody style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                    <span style={{ fontSize: '12px', color: '#a0a0a0', fontWeight: 500 }}>{card.title}</span>
                                                    <div style={{ padding: '6px', borderRadius: '6px', backgroundColor: card.bg }}>
                                                        <card.icon size={14} color={card.color} />
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: 'auto' }}>
                                                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, fontFamily: 'monospace' }}>{card.value}</p>
                                                    {card.sub && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                                            {card.positive ? (
                                                                <ArrowUpRight size={12} color="#10b981" />
                                                            ) : (
                                                                <ArrowDownRight size={12} color="#ef4444" />
                                                            )}
                                                            <span style={{ fontSize: '12px', fontWeight: 500, color: card.color }}>{card.sub}</span>
                                                            <span style={{ fontSize: '12px', color: '#a0a0a0' }}>vs current</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Chart */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.35, duration: 0.5 }}
                            >
                                <Card style={{ backgroundColor: "rgba(23, 24, 33, 1)", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(4px)" }}>
                                    <CardHeader style={{ backgroundColor: "rgba(23, 24, 33, 1)", borderBottom: "none", paddingBottom: "8px", paddingTop: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                        <CardTitle style={{ fontSize: "18px", fontWeight: "600", margin: 0, color: "#fff" }}>
                                            Actual vs Forecast
                                        </CardTitle>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ border: "1px solid rgba(246, 37, 90, 0.8)", color: "#F6255A", padding: "4px 8px", display: "flex", gap: "4px", alignItems: "center", borderRadius: "4px", fontSize: "12px" }}>
                                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#F6255A" }} />
                                                 Standard Forecast
                                            </div>
                                            {/* <div style={{ border: "1px solid rgba(16, 185, 129, 0.8)", color: "#10b981", padding: "4px 8px", display: "flex", gap: "4px", alignItems: "center", borderRadius: "4px", fontSize: "12px" }}>
                                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10b981" }} />
                                                Standard Forecast
                                            </div> */}
                                            {forecastData?.optimized_downloads_forecast && (
                                            <div style={{ border: "1px solid rgba(16, 185, 129, 0.8)", color: "#10b981", padding: "4px 8px", display: "flex", gap: "4px", alignItems: "center", borderRadius: "4px", fontSize: "12px" }}>
                                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10b981" }} />
                                                Optimized Forecast
                                            </div>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardBody>
                                        <Nav tabs style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", marginBottom: "16px" }}>
                                            <NavItem>
                                                <NavLink
                                                    className={classnames({ active: activeTab === 'downloads' })}
                                                    onClick={() => { setActiveTab('downloads'); }}
                                                    style={{ cursor: "pointer", backgroundColor: "transparent", color: activeTab === 'downloads' ? "#fff" : "#a0a0a0", border: "none", borderBottom: activeTab === 'downloads' ? "2px solid #F6255A" : "2px solid transparent", display: "flex", alignItems: "center", gap: "6px" }}
                                                >
                                                    <TrendingUp size={14} /> Downloads
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={classnames({ active: activeTab === 'revenue' })}
                                                    onClick={() => { setActiveTab('revenue'); }}
                                                    style={{ cursor: "pointer", backgroundColor: "transparent", color: activeTab === 'revenue' ? "#fff" : "#a0a0a0", border: "none", borderBottom: activeTab === 'revenue' ? "2px solid #F6255A" : "2px solid transparent", display: "flex", alignItems: "center", gap: "6px" }}
                                                >
                                                    <Activity size={14} /> Revenue
                                                </NavLink>
                                            </NavItem>
                                        </Nav>

                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId="downloads">
                                                <div style={{ height: "400px", width: "100%" }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={dlData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                                            <defs>
                                                                <linearGradient id="dlActualGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="dlForecastGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="dlOptimizedGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                            <XAxis
                                                                dataKey="date"
                                                                tick={{ fill: "#a0a0a0", fontSize: 11 }}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                interval="preserveStartEnd"
                                                            />
                                                            <YAxis
                                                                tick={{ fill: "#a0a0a0", fontSize: 11 }}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(v) => fmtNum(v)}
                                                                width={55}
                                                            />
                                                            <Tooltip content={<CustomTooltip isCurrency={false} />} />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="upper"
                                                                stroke="none"
                                                                fill="#10b981"
                                                                fillOpacity={0.08}
                                                                name="Upper Bound"
                                                                connectNulls={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="lower"
                                                                stroke="none"
                                                                fill="#1e1e2d"
                                                                fillOpacity={1}
                                                                name="Lower Bound"
                                                                connectNulls={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="actual"
                                                                stroke="#F6255A"
                                                                strokeWidth={2}
                                                                fill="url(#dlActualGrad)"
                                                                name="Actual"
                                                                connectNulls={false}
                                                                dot={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="forecast"
                                                                stroke="#F6255A"
                                                                strokeWidth={2}
                                                                strokeDasharray="6 3"
                                                                fill="url(#dlForecastGrad)"
                                                                name="Standard Forecast"
                                                                connectNulls={false}
                                                                dot={{ r: 3, fill: "#F6255A" }}
                                                            />
                                                            {forecastData?.optimized_downloads_forecast && (
                                                                <Area
                                                                    type="monotone"
                                                                    dataKey="optimized"
                                                                    stroke="#10b981"
                                                                    strokeWidth={2}
                                                                    strokeDasharray="none"
                                                                    fill="url(#dlOptimizedGrad)"
                                                                    name="Optimized Forecast"
                                                                    connectNulls={false}
                                                                    dot={{ r: 3, fill: "#10b981" }}
                                                                />
                                                            )}
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <div className="row mt-4">
                                                    <div className="col-6">
                                                        <div style={{ padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                                                            <p style={{ fontSize: "12px", color: "#a0a0a0", marginBottom: "4px" }}>Download Range</p>
                                                            <p style={{ fontSize: "14px", fontFamily: "monospace", fontWeight: 500, margin: 0 }}>
                                                                {fmtNum(forecastData?.downloads_range?.min || 0)} – {fmtNum(forecastData?.downloads_range?.max || 0)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div style={{ padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                                                            <p style={{ fontSize: "12px", color: "#a0a0a0", marginBottom: "4px" }}>7-Day Download Range</p>
                                                            <p style={{ fontSize: "14px", fontFamily: "monospace", fontWeight: 500, margin: 0 }}>
                                                                {fmtNum(forecastData?.downloads_forecast?.[0]?.value || 0)} → {fmtNum(forecastData?.downloads_forecast?.[forecastData.downloads_forecast?.length - 1]?.value || 0)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TabPane>

                                            <TabPane tabId="revenue">
                                                <div style={{ height: "400px", width: "100%" }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={revData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                                            <defs>
                                                                <linearGradient id="revActualGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="revForecastGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="revOptimizedGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                            <XAxis
                                                                dataKey="date"
                                                                tick={{ fill: "#a0a0a0", fontSize: 11 }}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                interval="preserveStartEnd"
                                                            />
                                                            <YAxis
                                                                tick={{ fill: "#a0a0a0", fontSize: 11 }}
                                                                tickLine={false}
                                                                axisLine={false}
                                                                tickFormatter={(v) => fmtCurrency(v)}
                                                                width={65}
                                                            />
                                                            <Tooltip content={<CustomTooltip isCurrency={true} />} />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="upper"
                                                                stroke="none"
                                                                fill="#10b981"
                                                                fillOpacity={0.08}
                                                                name="Upper Bound"
                                                                connectNulls={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="lower"
                                                                stroke="none"
                                                                fill="#1e1e2d"
                                                                fillOpacity={1}
                                                                name="Lower Bound"
                                                                connectNulls={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="actual"
                                                                stroke="#F6255A"
                                                                strokeWidth={2}
                                                                fill="url(#revActualGrad)"
                                                                name="Actual"
                                                                connectNulls={false}
                                                                dot={false}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="forecast"
                                                                stroke="#F6255A"
                                                                strokeWidth={2}
                                                                strokeDasharray="6 3"
                                                                fill="url(#revForecastGrad)"
                                                                name="Standard Forecast"
                                                                connectNulls={false}
                                                                dot={{ r: 3, fill: "#F6255A" }}
                                                            />
                                                            {forecastData?.optimized_revenue_forecast && (
                                                                <Area
                                                                    type="monotone"
                                                                    dataKey="optimized"
                                                                    stroke="#10b981"
                                                                    strokeWidth={2}
                                                                    strokeDasharray="none"
                                                                    fill="url(#revOptimizedGrad)"
                                                                    name="Optimized Forecast"
                                                                    connectNulls={false}
                                                                    dot={{ r: 3, fill: "#10b981" }}
                                                                />
                                                            )}
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <div className="row mt-4">
                                                    <div className="col-6">
                                                        <div style={{ padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                                                            <p style={{ fontSize: "12px", color: "#a0a0a0", marginBottom: "4px" }}>Revenue Range</p>
                                                            <p style={{ fontSize: "14px", fontFamily: "monospace", fontWeight: 500, margin: 0 }}>
                                                                {fmtCurrency(forecastData?.revenue_range?.min || 0)} – {fmtCurrency(forecastData?.revenue_range?.max || 0)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div style={{ padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                                                            <p style={{ fontSize: "12px", color: "#a0a0a0", marginBottom: "4px" }}>7-Day Revenue Range</p>
                                                            <p style={{ fontSize: "14px", fontFamily: "monospace", fontWeight: 500, margin: 0 }}>
                                                                {fmtCurrency(forecastData?.revenue_forecast?.[0]?.value || 0)} → {fmtCurrency(forecastData?.revenue_forecast?.[forecastData.revenue_forecast?.length - 1]?.value || 0)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TabPane>
                                        </TabContent>
                                    </CardBody>
                                </Card>
                            </motion.div>

                            {/* Suggestions and Insights */}
                            {(forecastData?.suggestions?.length > 0 || forecastData?.insights?.length > 0) && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 0.5 }}
                                    className="mt-4"
                                >
                                    <div className="row">
                                        {forecastData?.suggestions?.length > 0 && (
                                            <div className="col-md-6 mb-3">
                                                <Card style={{ backgroundColor: "rgba(23, 24, 33, 1)", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(4px)" }}>
                                                    <CardHeader style={{ backgroundColor: "rgba(23, 24, 33, 1)", borderBottom: "none", paddingBottom: "12px", paddingTop: "16px" }}>
                                                        <CardTitle style={{ fontSize: "16px", fontWeight: "600", margin: 0, color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
                                                            <Lightbulb size={16} color="#fbbf24" />
                                                            AI Suggestions
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardBody style={{ paddingTop: "0" }}>
                                                        {forecastData.suggestions.map((suggestion, index) => (
                                                            <div key={index} style={{ marginBottom: "12px", padding: "12px", borderRadius: "6px", backgroundColor: "rgba(251, 191, 36, 0.1)", border: "1px solid rgba(251, 191, 36, 0.2)" }}>
                                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                                                                    <span style={{ fontSize: "12px", fontWeight: "600", color: "#fbbf24", textTransform: "uppercase" }}>{suggestion.metric}</span>
                                                                    <span style={{ fontSize: "11px", color: "#10b981", fontWeight: "500" }}>{suggestion.impact}</span>
                                                                </div>
                                                                <p style={{ fontSize: "13px", color: "#ef4444", margin: "4px 0", fontWeight: "500" }}>{suggestion.issue}</p>
                                                                <p style={{ fontSize: "12px", color: "#a0a0a0", margin: 0 }}>{suggestion.action}</p>
                                                            </div>
                                                        ))}
                                                    </CardBody>
                                                </Card>
                                            </div>
                                        )}
                                        
                                        {forecastData?.insights?.length > 0 && (
                                            <div className="col-md-6 mb-3">
                                                <Card style={{ backgroundColor: "rgba(23, 24, 33, 1)", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(4px)" }}>
                                                    <CardHeader style={{ backgroundColor: "rgba(23, 24, 33, 1)", borderBottom: "none", paddingBottom: "12px", paddingTop: "16px" }}>
                                                        <CardTitle style={{ fontSize: "16px", fontWeight: "600", margin: 0, color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
                                                            <Activity size={16} color="#3b82f6" />
                                                            Key Insights
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardBody style={{ paddingTop: "0" }}>
                                                        {forecastData.insights.map((insight, index) => (
                                                            <div key={index} style={{ marginBottom: "8px", padding: "10px", borderRadius: "6px", backgroundColor: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                                                                <p style={{ fontSize: "13px", color: "#fff", margin: 0, lineHeight: "1.4" }}>{insight}</p>
                                                            </div>
                                                        ))}
                                                    </CardBody>
                                                </Card>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </>
                    )}
                </div>

                {/* KeyQuest AI Input Section — below main content, always visible when data loaded */}
                {!loading && !error && forecastData && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                        style={{ marginTop: 32, marginBottom: 32 }}
                    >
                        {/* Divider */}
                        <div style={{ borderTop: "1px solid rgba(246,37,90,0.25)", marginBottom: 24 }} />

                        {/* Card wrapper */}
                        <div style={{
                            backgroundColor: "rgba(23,24,33,1)",
                            border: "1px solid rgba(246,37,90,0.3)",
                            borderRadius: 16,
                            padding: "20px 24px",
                            boxShadow: "0 4px 24px rgba(246,37,90,0.06)",
                        }}>
                            {/* Header with icon */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
                                <Zap style={{ width: 18, height: 18, color: "#F6255A" }} />
                                <span style={{
                                    fontSize: 13,
                                    fontFamily: "JetBrains Mono, monospace",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    color: "#F6255A",
                                    fontWeight: 600,
                                }}>
                                    KeyQuest AI
                                </span>
                                <span style={{ fontSize: 12, color: "#6b6b80", marginLeft: 4 }}>
                                    · Ask anything about this forecast
                                </span>
                            </div>

                            {/* Input row */}
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                <textarea
                                    rows={1}
                                    placeholder="Ask any question about this forecast…"
                                    value={aiQuery}
                                    onChange={(e) => setAiQuery(e.target.value)}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey && aiQuery?.trim().length > 0) {
                                            e.preventDefault();
                                            setAskOpen(true);
                                        }
                                    }}
                                    style={{
                                        flex: 1,
                                        resize: "none",
                                        minHeight: 46,
                                        maxHeight: 120,
                                        padding: "12px 14px",
                                        fontSize: 14,
                                        lineHeight: 1.5,
                                        borderRadius: 12,
                                        backgroundColor: isFocused ? "rgba(34,36,50,1)" : "rgba(14,15,22,1)",
                                        border: isFocused ? "1px solid #F6255A" : "1px solid rgba(255,255,255,0.07)",
                                        outline: "none",
                                        color: "#FFFFFF",
                                        transition: "border-color 0.2s, background-color 0.2s",
                                        fontFamily: "inherit",
                                    }}
                                />
                                <Button
                                    color="primary"
                                    disabled={!aiQuery || aiQuery.trim().length === 0}
                                    onClick={() => setAskOpen(true)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "0 18px",
                                        height: 46,
                                        backgroundColor: "#F6255A",
                                        borderColor: "#F6255A",
                                        borderRadius: 12,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        cursor: !aiQuery ? "not-allowed" : "pointer",
                                        opacity: !aiQuery ? 0.5 : 1,
                                        flexShrink: 0,
                                        transition: "opacity 0.2s",
                                    }}
                                >
                                    <Send style={{ width: 15, height: 15 }} />
                                    Ask AI
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Chat modal for Ask AI */}
                <ChatbotUI
                    toggle={askOpen}
                    togglemodal={toggleAsk}
                    initialPrompt={aiQuery}
                    pageDescription={`Ask AI about ${appName || 'this app'}'s forecasting`}
                    aiReport={forecastData ? JSON.stringify(forecastData) : ''}
                />
            </div>
        </div>
    );
};

export default Forecasting;
